import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ScanFace, ExternalLink, ShieldCheck } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { idvMeta } from '../../lib/identityStatus';
import { onboardingReviewAPI } from '../../lib/onboardingReviewAPI';
import { authAPI } from '../../lib/api';

const fmt = (iso) => (iso ? new Date(iso).toLocaleString('en-AU') : '—');

const ManualDialog = ({ clientId, open, onClose, onDone }) => {
  const [f, setF] = useState({ method: 'Certified ID sighted', reason: '', file: null });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    const fd = new FormData();
    fd.append('method', f.method); fd.append('reason', f.reason);
    if (f.file) fd.append('file', f.file);
    setBusy(true);
    try { await onboardingReviewAPI.manualIdentity(clientId, fd); toast.success('Manual ID verification recorded'); onDone(); onClose(); } catch (e) { toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || 'Could not record verification'); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="idv-manual-dialog">
        <DialogHeader><DialogTitle>Record manual ID verification</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground">Exception for clients who cannot verify online. This is recorded as a manual verification — it never marks the Didit check as approved — and is audited.</p>
        <label className="text-sm">Method<Input value={f.method} onChange={(e) => setF({ ...f, method: e.target.value })} data-testid="idv-manual-method" /></label>
        <label className="text-sm">Reason (required)<Textarea value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} placeholder="e.g. Client has no smartphone; original passport sighted in office and copy certified" data-testid="idv-manual-reason" /></label>
        <label className="text-sm">Evidence (optional, virus-scanned)<Input type="file" onChange={(e) => setF({ ...f, file: e.target.files?.[0] || null })} data-testid="idv-manual-file" /></label>
        <DialogFooter><Button disabled={busy || f.reason.trim().length < 10 || f.method.trim().length < 3} onClick={save} data-testid="idv-manual-submit">Record verification</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const IdentityReviewPanel = ({ clientId, idv, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [canOverride, setCanOverride] = useState(false);
  useEffect(() => { authAPI.getMe().then((r) => setCanOverride(['owner', 'admin'].includes(r.data.active_membership?.role || r.data.user?.role))).catch(() => {}); }, []);
  const m = idvMeta(idv.didit_status, idv.id_verification_status, idv.id_verification_manual);
  const summary = Object.entries(idv.decision_summary || {}).filter(([, v]) => v !== null && v !== '' && v !== undefined);
  return (
    <Card data-testid="idv-panel">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2 text-lg"><ScanFace className="h-5 w-5" /> Identity Verification</CardTitle>
        {canOverride && !m.ok && <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="idv-manual-open"><ShieldCheck className="mr-1 h-4 w-4" />Record manual ID verification</Button>}
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        <div className="flex items-center justify-between"><span className="text-muted-foreground">Status</span><Badge className={m.color} data-testid="idv-status">{m.label}</Badge></div>
        {m.detail && <p className="rounded-md bg-teal-50 p-2 text-xs text-teal-900" data-testid="idv-manual-detail">{m.detail} · {idv.id_verification_manual.method} · {fmt(idv.id_verification_manual.at)}</p>}
        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
          <span className="text-muted-foreground">Didit status</span><span data-testid="idv-didit-status">{idv.didit_status || 'No session yet'}</span>
          <span className="text-muted-foreground">Environment</span><span data-testid="idv-environment">{idv.environment || '—'}</span>
          <span className="text-muted-foreground">Session started</span><span>{fmt(idv.session_created_at)}</span>
          <span className="text-muted-foreground">Last update</span><span>{fmt(idv.updated_at)}</span>
          {idv.session_id && <><span className="text-muted-foreground">Session</span><a href={idv.console_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-primary underline" data-testid="idv-console-link">{idv.session_id.slice(0, 8)}… in Didit console <ExternalLink className="h-3 w-3" /></a></>}
        </div>
        {summary.length > 0 && <div className="grid grid-cols-1 gap-x-4 gap-y-1 border-t pt-2 sm:grid-cols-2" data-testid="idv-decision-summary">
          {summary.map(([k, v]) => <div key={k} className="text-xs"><span className="text-muted-foreground/70">{k.replace(/_/g, ' ')}: </span>{typeof v === 'boolean' ? (v ? 'Yes' : 'No') : String(v)}</div>)}</div>}
      </CardContent>
      <ManualDialog clientId={clientId} open={open} onClose={() => setOpen(false)} onDone={onChanged} />
    </Card>
  );
};
